/**
 * V6.2.A Voice GM (2026-06-02) — per-campaign visited paragraph tracking.
 *
 * Used by useAutoNarrate's 'first-visit' mode to skip the full paragraph
 * narration on revisits (and play the brief cue instead). Persisted per
 * campaign in localStorage so the player's progress survives reloads.
 *
 * Storage key:  briefing-room:visited:{campaignId}
 * Storage shape: JSON-encoded string[] of view keys ('briefing', 'p:5',
 *                'p:17', 'ending') — same key format produced by
 *                ScenarioRunner.autoNarrateView.
 *
 * Pattern matches useVoiceLog (also per-campaign localStorage) so the
 * lifecycle is familiar to readers.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'briefing-room:visited:';

function storageKey(campaignId: string): string {
  return `${STORAGE_PREFIX}${campaignId}`;
}

function readStored(campaignId: string): Set<string> {
  if (typeof window === 'undefined' || campaignId.length === 0) {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(storageKey(campaignId));
    if (raw === null) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((x) => typeof x === 'string'));
    }
  } catch {
    // corrupted or unreadable localStorage — start fresh
  }
  return new Set();
}

function writeStored(campaignId: string, value: Set<string>): void {
  if (typeof window === 'undefined' || campaignId.length === 0) return;
  try {
    window.localStorage.setItem(
      storageKey(campaignId),
      JSON.stringify([...value]),
    );
  } catch {
    // quota or privacy mode — degrade quietly
  }
}

export interface UseVisitedParagraphsResult {
  /** Snapshot of currently-visited view keys. Reference-stable per render
   *  unless markVisited / reset is called. */
  visited: ReadonlySet<string>;
  /** True when the key has been visited at least once this campaign. */
  isVisited(key: string): boolean;
  /** Record a view-key as visited. No-op if already in the set. */
  markVisited(key: string): void;
  /** Clear all visited entries for this campaign. */
  reset(): void;
}

export function useVisitedParagraphs(campaignId: string): UseVisitedParagraphsResult {
  const [visited, setVisited] = useState<Set<string>>(() => readStored(campaignId));

  // When the campaignId changes (e.g. user switches campaigns without a
  // remount), re-hydrate from the new key.
  useEffect(() => {
    setVisited(readStored(campaignId));
  }, [campaignId]);

  const isVisited = useCallback(
    (key: string): boolean => visited.has(key),
    [visited],
  );

  const markVisited = useCallback(
    (key: string): void => {
      if (key.length === 0 || campaignId.length === 0) return;
      setVisited((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        writeStored(campaignId, next);
        return next;
      });
    },
    [campaignId],
  );

  const reset = useCallback((): void => {
    setVisited(new Set());
    writeStored(campaignId, new Set());
  }, [campaignId]);

  return { visited, isVisited, markVisited, reset };
}
